import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import type { ProgressMessage, ReviewMedia } from '@/lib/reviews';
import ImageUpload from './image-upload';
import StarRating from './star-rating';

interface ReviewFormProps {
  open: boolean;
  isEditing: boolean;
  itemName: string;
  submitting: boolean;
  formData: {
    rating: number;
    content: string;
  };
  errors: {
    rating: string;
    content: string;
    images: string;
  };
  uploadedImages: ReviewMedia[];
  uploadingFiles: Map<string, File>;
  previewUrls: Map<string, string>;
  isDragging: boolean;
  progressMessages: ProgressMessage[];
  onClose: () => void;
  onCancel?: () => void;
  onSubmit: (event: React.FormEvent) => void;
  onRatingChange: (rating: number) => void;
  onContentChange: (content: string) => void;
  onFileSelect: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onDragOver: (event: React.DragEvent) => void;
  onDragLeave: (event: React.DragEvent) => void;
  onDrop: (event: React.DragEvent) => void;
  onDeleteUploadedImage: (mediaId: string) => void;
  onRemoveUploadingFile: (fileId: string) => void;
  onRemoveProgressMessage: (id: string) => void;
}

export default function ReviewForm({
  open,
  isEditing,
  itemName,
  submitting,
  formData,
  errors,
  uploadedImages,
  uploadingFiles,
  previewUrls,
  isDragging,
  progressMessages,
  onClose,
  onCancel,
  onSubmit,
  onRatingChange,
  onContentChange,
  onFileSelect,
  onDragOver,
  onDragLeave,
  onDrop,
  onDeleteUploadedImage,
  onRemoveUploadingFile,
  onRemoveProgressMessage,
}: ReviewFormProps) {
  const handleContentInput = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    onContentChange(event.target.value);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader className="text-right">
          <DialogTitle>{isEditing ? 'עריכת ביקורת' : 'הוספת ביקורת'}</DialogTitle>
          <DialogDescription>שתפו את החוויה שלכם עם {itemName}</DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="space-y-4 px-4">
          {/* Rating */}
          <div className="space-y-2 text-right">
            <Label htmlFor="rating">דירוג</Label>
            <div className="flex flex-row-reverse justify-end gap-1 py-2">
              <StarRating
                rating={formData.rating}
                interactive={true}
                size="lg"
                onRatingChange={onRatingChange}
              />
            </div>
            {errors.rating && <p className="text-right text-sm text-red-500">{errors.rating}</p>}
          </div>

          {/* Content */}
          <div className="space-y-2 text-right">
            <Label htmlFor="content">תוכן הביקורת</Label>
            <Textarea
              id="content"
              value={formData.content}
              onChange={handleContentInput}
              placeholder="ספרו על החוויה שלכם עם המוצר..."
              rows={4}
              className={errors.content ? 'border-red-500 text-right' : 'text-right'}
            />
            {errors.content && <p className="text-right text-sm text-red-500">{errors.content}</p>}
          </div>

          {/* Image Upload */}
          <ImageUpload
            uploadedImages={uploadedImages}
            uploadingFiles={uploadingFiles}
            previewUrls={previewUrls}
            isDragging={isDragging}
            progressMessages={progressMessages}
            errors={errors.images}
            onFileSelect={onFileSelect}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            onDeleteUploadedImage={onDeleteUploadedImage}
            onRemoveUploadingFile={onRemoveUploadingFile}
            onRemoveProgressMessage={onRemoveProgressMessage}
          />

          {/* Submit Button */}
          <div className="flex flex-row-reverse gap-2 pt-4">
            <Button type="button" variant="outline" onClick={onCancel || onClose}>
              ביטול
            </Button>
            <Button
              type="submit"
              disabled={submitting || uploadingFiles.size > 0}
              className="flex-1"
            >
              {submitting
                ? 'שומר...'
                : uploadingFiles.size > 0
                  ? `מעלה תמונות... (${uploadingFiles.size})`
                  : isEditing
                    ? 'עדכן ביקורת'
                    : 'פרסם ביקורת'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
